import { ORG_HOME_PATH } from './rails';
import type { NavMenuItem, NavRailDef, RailId } from './types';

/** Per-rail page visibility. Paths are catalog paths the user can already open. */
export interface NavWorkspaceRail {
  id: string;
  hiddenItems: string[];
  itemOrder: string[];
  /** Unused. The first visible page in itemOrder is the rail click target. */
  homePath: string;
}

export interface NavWorkspaceEntry {
  id: string;
  name: string;
  hidden: string[];
  order: string[];
  rails: NavWorkspaceRail[];
  /**
   * Page opened after login and from `/`.
   * Empty, hidden, or no longer permitted falls back to the dashboard.
   */
  startPath?: string;
}

export interface NavWorkspacePreference {
  activeId: string;
  workspaces: NavWorkspaceEntry[];
  saved: boolean;
}

const railPref = (workspace: NavWorkspaceEntry | undefined, railId: RailId): NavWorkspaceRail | undefined =>
  workspace?.rails?.find((entry) => entry.id === railId);

/** Catalog order, skipping pages this workspace hid. Unknown paths are ignored. */
export const workspaceMenuItems = (
  railId: RailId,
  items: NavMenuItem[],
  workspace: NavWorkspaceEntry | undefined,
): NavMenuItem[] => {
  const pref = railPref(workspace, railId);
  if (!pref) return items;
  const hidden = new Set(pref.hiddenItems);
  const byPath = new Map(items.map((item) => [item.path, item]));
  const seen = new Set<string>();
  const ordered: NavMenuItem[] = [];
  for (const path of pref.itemOrder) {
    const item = byPath.get(path);
    if (!item || hidden.has(path) || seen.has(path)) continue;
    seen.add(path);
    ordered.push(item);
  }
  for (const item of items) {
    if (hidden.has(item.path) || seen.has(item.path)) continue;
    ordered.push(item);
  }
  return ordered;
};

export const workspaceRailPath = (
  railId: RailId,
  items: NavMenuItem[],
  fallback: string,
  workspace: NavWorkspaceEntry | undefined,
): string => {
  const visible = workspaceMenuItems(railId, items, workspace);
  return visible[0]?.path ?? fallback;
};

/**
 * Rail order from the workspace, then drop a rail whose pages are all hidden.
 * If that would leave the sidebar empty, keep the permission-filtered catalog.
 */
export const applyNavWorkspace = (
  rails: NavRailDef[],
  itemsByRail: Partial<Record<RailId, NavMenuItem[]>>,
  workspace: NavWorkspaceEntry | undefined,
): NavRailDef[] => {
  if (!workspace) return rails;
  const hidden = new Set(workspace.hidden);
  const byId = new Map(rails.map((rail) => [rail.id, rail]));
  const placed = new Set<RailId>();
  const ordered: NavRailDef[] = [];

  const keep = (rail: NavRailDef): boolean => {
    if (hidden.has(rail.id) || placed.has(rail.id)) return false;
    const items = itemsByRail[rail.id] ?? [];
    if (items.length === 0) return true;
    return workspaceMenuItems(rail.id, items, workspace).length > 0;
  };

  for (const id of workspace.order) {
    const rail = byId.get(id as RailId);
    if (!rail || !keep(rail)) continue;
    placed.add(rail.id);
    ordered.push(rail);
  }
  for (const rail of rails) {
    if (!keep(rail)) continue;
    placed.add(rail.id);
    ordered.push(rail);
  }
  return ordered.length > 0 ? ordered : rails;
};

/**
 * Pages this workspace still shows. The built-in default workspace does not
 * hide or reorder the sidebar, so every permitted page stays selectable.
 */
export const workspaceVisiblePages = (
  rails: NavRailDef[],
  itemsByRail: Partial<Record<RailId, NavMenuItem[]>>,
  workspace: NavWorkspaceEntry | undefined,
): NavMenuItem[] => {
  const sidebarWorkspace = workspace?.id === 'default' ? undefined : workspace;
  const visibleRails = applyNavWorkspace(rails, itemsByRail, sidebarWorkspace);
  const pages: NavMenuItem[] = [];
  const seen = new Set<string>();
  for (const rail of visibleRails) {
    const items = workspaceMenuItems(rail.id, itemsByRail[rail.id] ?? [], sidebarWorkspace);
    for (const item of items) {
      if (seen.has(item.path)) continue;
      seen.add(item.path);
      pages.push(item);
    }
  }
  return pages;
};

/** Active workspace start page, or the dashboard when that page cannot be opened. */
export const resolveWorkspaceStartPath = (
  workspace: NavWorkspaceEntry | undefined,
  pages: { path: string }[],
): string => {
  const start = workspace?.startPath?.trim() ?? '';
  if (!start || start === ORG_HOME_PATH) return ORG_HOME_PATH;
  if (!pages.some((page) => page.path === start)) return ORG_HOME_PATH;
  return start;
};
